/**
 * @file    SessionExtension.js
 * 
 * This module is a lease based registry for preventing automatic logouts.
 * 
 * Consumers can extend the session by calling extendLoginSession with a duration, 
 * which creates a lease. Infinity is supported as a duration. A cancel handle 
 * is returned which cancels the lease prematurely.
 * 
 * Each lease is independent, and lasts until it is cancelled or the
 * duration expires. The session is extended iff at least one lease exists,
 * meaning the extension lasts until all leases are gone.
 * 
 * Consumers who enforce automatic logouts can listen to the module's state using
 * subscribe(callback). The callback receives a boolean which indicates whether the
 * current session is being extended (iff at least one lease exists). It can be
 * removed using the cancel handle returned by subscribe.
 * 
 * Subscriber callbacks are invoked:
 *   - once during the subscribe function
 *   - once for each future state change (0 leases -> N+ leases or N+ leases -> 0 leases) 
 *
 * @author      Joseph Acernese, joseph@distributive.network
 * @date        July 2026
 */
'use strict';

const leases = new Map();      // id -> leaseTimeout
const subscribers = new Set(); // callbacks invoked with (isExtended: boolean)
let nextLeaseId = 0;
let wasExtended = false;       // boolean used to detect extension state transitions

/**
 * Create a lease requesting the session be kept alive for a given duration
 * 
 * Logout suppression is indirect: enforcers listen for leases via subscribe(),
 * and pause logout behaviour while any exist
 *
 * @param   {number} durationMs  lease duration; Infinity supported
 * @returns {function}           cancel handle used to remove the lease; no-ops if called more than once
 */
export function extendLoginSession(durationMs)
{
  if (!(durationMs > 0))
    throw new TypeError(`durationMs must be a positive number or Infinity, got ${durationMs}`);

  const id = nextLeaseId++;
  let leaseTimeout = null;

  if (durationMs !== Infinity)
    leaseTimeout = setTimeout(() => removeLease(id), durationMs);

  leases.set(id, leaseTimeout);
  notify();

  return () => removeLease(id);
}

/**
 * Subscribe to session extension state updates. 
 * The callback receives a boolean indicating if the session is currently extended,
 * it is invoked immediately, then whenever the extension state changes.
 * 
 * @param   {function} callback  callback function which will receive a single boolean argument
 * @returns {function}           unsubscribe handle which deletes the callback; no-ops if called more than once
 */
export function subscribe(callback)
{
  if (typeof callback !== 'function')
    throw new TypeError(`subscribe expects a function, got ${typeof callback}`);
  
  const isExtended = isSessionExtended();
  callback(isExtended);

  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * @returns {boolean}  whether the session is being extended
 */
function isSessionExtended()
{
  return leases.size > 0;
}

/**
 * Removes the lease and clears its timeout; no-ops if the id is invalid
 * @param {number} id 
 */
function removeLease(id)
{
  if (!leases.has(id))
    return;
  //leaseTimeout will be null if the extension was for Infinity
  const leaseTimeout = leases.get(id);
  clearTimeout(leaseTimeout);
  leases.delete(id);
  notify();
}

/**
 * Notifies subscribers of changes to the session extension state.
 * Callbacks are isolated, allowing all to be invoked even if some throw.
 */
function notify()
{
  const isExtended = isSessionExtended();
  if (isExtended === wasExtended)
    return;
  wasExtended = isExtended;

  for (const callback of subscribers)
  {
    try
    {
      callback(isExtended);
    }
    catch (err)
    {
      console.error('subscriber callback threw', err);
    }
  }
}

import * as selfExports from './SessionExtension.js';
if (globalThis.modules)
{
  if (!globalThis.modules.portal)
    globalThis.modules.portal = {};
  globalThis.modules.portal.session = selfExports; /* dcp-client dom-tk */
}
